import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO, { breadcrumbJsonLd, collectionPageJsonLd } from '../components/SEO';
import useCases from '../data/useCases';

/**
 * /use-cases — index hub summarising every aviation-maintenance use case.
 *
 * Each entry is pulled from the shared `data/useCases.js` source, so adding
 * a new use case there automatically:
 *   - lists it here,
 *   - creates its dedicated /use-cases/<slug> page,
 *   - includes it in the sitemap + prerender pipeline.
 */
const UseCasesIndexPage = ({ onRequestDemo, onRequestQuote }) => {
  const canonicalUrl = 'https://ctrl.aero/use-cases';

  // Pull the first ROI/savings-flavoured application line for each use case
  // as a card highlight — falls back to the first application if none match.
  const pickHighlight = (uc) => {
    const items = (uc.applications || []).map((a) =>
      typeof a === 'string' ? a : a?.text
    );
    const roi = items.find((s) =>
      /\b(ROI|saved|\$|hour|min|%)\b/i.test(s || '')
    );
    return roi || items[0] || '';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Aviation Maintenance Use Cases | ctrl.aero"
        description="Real-world MRO case studies for ultrasonic troubleshooting and acoustic imaging — engine diagnostics, valve inspections, landing-gear inspections, and hydraulic systems."
        canonical={canonicalUrl}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'Use Cases', url: canonicalUrl },
          ]),
          collectionPageJsonLd({
            name: 'Aviation Maintenance Use Cases',
            description:
              'Case studies documenting how the CTRL UL101 and FOTRIC acoustic imaging cameras solve real aviation-MRO troubleshooting problems.',
            url: canonicalUrl,
            items: useCases.map((uc) => ({
              type: 'Article',
              name: `${uc.title} — ${uc.caseStudy}`,
              url: `https://ctrl.aero/use-cases/${uc.slug}`,
            })),
          }),
        ]}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1e2d3b] via-[#243447] to-[#1e2d3b] pt-24 pb-16 sm:pt-28 sm:pb-20 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl"
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link
            to="/"
            className="inline-flex items-center text-white/70 hover:text-white text-sm mb-6 transition-colors"
            data-testid="use-cases-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>

          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-300 text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6">
            Aviation MRO Case Studies
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            Real Aircraft.<br className="hidden sm:block" /> Real Faults.{' '}
            <span className="text-blue-300">Solved Faster.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-blue-100/85 leading-relaxed">
            Every use case below is drawn from a documented MRO troubleshooting job on an
            in-service commercial aircraft. Each links to the full case study with
            timings, savings, and the exact CTRL UL101 or FOTRIC workflow that resolved
            it.
          </p>
        </div>
      </section>

      {/* Grid of use cases */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              const highlight = pickHighlight(uc);
              return (
                <Link
                  key={uc.slug}
                  to={`/use-cases/${uc.slug}`}
                  className="group relative flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300"
                  data-testid={`use-case-card-${uc.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 sm:h-56 overflow-hidden bg-slate-200">
                    {uc.image && (
                      <img
                        src={uc.image}
                        alt={`${uc.title} — ${uc.caseStudy}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1e2d3b]/70 via-[#1e2d3b]/10 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                      <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-md">
                        <Icon className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs font-semibold tracking-[0.16em] uppercase text-blue-200">
                        Case Study
                      </p>
                      <p className="text-white text-sm sm:text-base font-semibold leading-snug drop-shadow">
                        {uc.caseStudy}
                      </p>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 flex flex-col p-6 sm:p-7">
                    <h2 className="text-xl sm:text-2xl font-bold text-[#1e2d3b] tracking-tight group-hover:text-blue-600 transition-colors">
                      {uc.title}
                    </h2>
                    <p className="mt-3 text-slate-600 text-base leading-relaxed flex-1">
                      {uc.tagline}
                    </p>

                    {highlight && (
                      <div className="mt-5 inline-flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-900 font-medium">
                        <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-orange-500" />
                        <span>{highlight}</span>
                      </div>
                    )}

                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                      Read the full case study
                      <ArrowRight
                        className="w-4 h-4 transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] tracking-tight">
            Have a fault we haven&rsquo;t documented yet?
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Our team supports airlines and MROs across the fleet. Book a demo with the
            UL101 or a FOTRIC acoustic imager, or ask for a written quote sized to your
            operation.
          </p>
          <div className="mt-8 cta-group max-w-lg mx-auto">
            <Button
              onClick={onRequestDemo}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-[15px] font-semibold"
              data-testid="use-cases-hub-request-demo"
            >
              Request Demo
            </Button>
            <Button
              onClick={onRequestQuote}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm sm:text-[15px] font-semibold"
              data-testid="use-cases-hub-request-quote"
            >
              Request Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UseCasesIndexPage;
